'use client';

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRouter } from '@/i18n/navigation';
import { cn, generateUUID } from '@/lib/utils';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { isRightToLeft } from '@/utils/rtl';

type PricingView = 'options' | 'faq';

interface CharacterPricingPanelProps {
  showTermsLink?: boolean;
  onPackageSelect?: (packageId: string) => void;
  onTermsClick?: () => void;
}

interface PricingPackage {
  id: string;
  packageKey: string;
  charactersTotal: number;
  priceMinor: number;
  isRecommended: boolean;
}

interface PricingApiResponse {
  currency: string;
  packages: PricingPackage[];
}

interface CheckoutApiResponse {
  checkoutUrl: string;
}

interface CheckoutPrepareRedirectDetail {
  restoreKey: string;
}

const CHECKOUT_PREPARE_REDIRECT_EVENT = 'checkout-prepare-redirect';

function createRestoreKey(): string {
  return `${Date.now()}-${generateUUID()}`;
}

export function CharacterPricingPanel({
  showTermsLink = true,
  onPackageSelect,
  onTermsClick
}: CharacterPricingPanelProps) {
  const t = useTranslations('pricing');
  const locale = useLocale();
  const isRtl = isRightToLeft(locale);
  const router = useRouter();
  const [view, setView] = useState<PricingView>('options');
  const [openFaqItem, setOpenFaqItem] = useState<string>('refunds');
  const [creatingPackageKey, setCreatingPackageKey] = useState<string | null>(null);

  const { data: pricingData, isLoading } = useQuery({
    queryKey: ['pricing-packages'],
    queryFn: async () => {
      const response = await fetch('/api/pricing');
      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }
      return response.json() as Promise<PricingApiResponse>;
    },
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12,
  });

  const packages = pricingData?.packages ?? [];
  const currency = pricingData?.currency ?? 'EUR';

  const faqItems = ['refunds', 'expiry', 'invoice'] as const;

  const sortedByCharacters = [...packages].sort((a, b) => a.charactersTotal - b.charactersTotal);
  const lowestTier = sortedByCharacters[0];
  const lowestUnitPrice = lowestTier ? lowestTier.priceMinor / lowestTier.charactersTotal : 0;

  const formatMoney = (priceMinor: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(priceMinor / 100);
  };

  const handleTermsClick = () => {
    onTermsClick?.();
    router.push('/terms');
  };

  const handleBuyNowClick = async (packageKey: string) => {
    if (onPackageSelect) {
      onPackageSelect(packageKey);
      return;
    }

    try {
      setCreatingPackageKey(packageKey);
      const restoreKey = createRestoreKey();

      window.dispatchEvent(new CustomEvent<CheckoutPrepareRedirectDetail>(CHECKOUT_PREPARE_REDIRECT_EVENT, {
        detail: { restoreKey }
      }));

      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ packageKey, restoreKey }),
      });

      const responseBody = await response.json() as CheckoutApiResponse & { error?: string };
      if (!response.ok || !responseBody.checkoutUrl) {
        throw new Error(responseBody.error || 'Failed to start checkout');
      }

      window.location.assign(responseBody.checkoutUrl);
    } catch (error) {
      console.error('Failed to start checkout', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCreatingPackageKey(null);
    }
  };

  return (
    <div className="overflow-hidden">
      <div
        className={cn(
          'flex w-[200%] transition-transform duration-300 ease-in-out',
          view === 'options' ? 'translate-x-0' : isRtl ? 'translate-x-1/2' : '-translate-x-1/2'
        )}
      >
        <div className="w-1/2 pr-2">
          <div className="space-y-3">
            {isLoading && (
              <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
                {t('loading')}
              </div>
            )}
            {!isLoading && packages.map((entry) => {
              const unitPrice = entry.priceMinor / entry.charactersTotal;
              const savingsPercent = lowestUnitPrice > 0
                ? Math.round((1 - (unitPrice / lowestUnitPrice)) * 100)
                : 0;

              return (
                <button
                  key={entry.id}
                  type="button"
                  className={cn(
                    'w-full rounded-lg border bg-card p-4 text-left transition-all hover:bg-accent/30 no-focus disabled:cursor-not-allowed disabled:opacity-70',
                    entry.isRecommended
                      ? 'border-yellow-500/70 hover:border-yellow-500 dark:border-yellow-400/70 dark:hover:border-yellow-300'
                      : 'hover:border-muted-foreground/30'
                  )}
                  onClick={() => void handleBuyNowClick(entry.packageKey)}
                  disabled={creatingPackageKey !== null}
                  aria-label={`${t('buyNow')} ${entry.charactersTotal.toLocaleString(locale)} ${t('characters')}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          'text-[1.3rem] font-semibold',
                          entry.isRecommended && 'text-yellow-700 dark:text-yellow-300'
                        )}>
                          {formatMoney(entry.priceMinor)}
                        </div>
                        {savingsPercent > 0 && (
                          <Badge className="bg-green-600 text-white hover:bg-green-600/90 dark:bg-green-500 dark:text-green-950">
                            {t('savePercent', { percent: savingsPercent })}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-foreground">
                        {entry.charactersTotal.toLocaleString(locale)} {t('characters')}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {entry.isRecommended && (
                        <Badge className="uppercase tracking-wide bg-yellow-500 text-yellow-950 hover:bg-yellow-500/90 dark:bg-yellow-400 dark:text-yellow-950">
                          {t('recommended')}
                        </Badge>
                      )}

                      {creatingPackageKey === entry.packageKey && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            <div className="flex items-center justify-center gap-6 pt-1 text-sm">
              <button
                type="button"
                onClick={() => setView('faq')}
                className="text-muted-foreground hover:text-foreground transition-colors no-focus"
              >
                {t('faq')}
              </button>
              {showTermsLink && (
                <button
                  type="button"
                  onClick={handleTermsClick}
                  className="text-muted-foreground hover:text-foreground transition-colors no-focus"
                >
                  {t('terms')}
                </button>
              )}
            </div>

            <p className="pt-1 text-center text-xs text-muted-foreground">
              {t('taxesIncludedNotice')}
            </p>
          </div>
        </div>

        <div className="w-1/2 pl-2">
          <div className="rounded-lg border bg-card">
            <ScrollArea className="p-4">
              <div className="space-y-3">
                {faqItems.map((key) => {
                  const isOpen = openFaqItem === key;
                  return (
                    <div key={key} className="rounded-md border p-3">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 text-left no-focus"
                        onClick={() => setOpenFaqItem(isOpen ? '' : key)}
                      >
                        <span className="text-sm font-medium">{t(`faqItems.${key}.question`)}</span>
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {isOpen && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          {t(`faqItems.${key}.answer`)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="border-t p-3">
              <Button
                type="button"
                variant="ghost"
                className="w-full no-focus"
                onClick={() => setView('options')}
              >
                {t('backToOptions')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
